import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Gift, X, ArrowRight } from "lucide-react";

/**
 * Sticky, dismissible referral banner shown to landlord users on the
 * dashboard. Dismissed state persists in localStorage with a 7-day TTL —
 * so a user who dismisses today sees it again next week (keeps the $50
 * offer top of mind without being obnoxious).
 */
const DISMISS_KEY = "crc_referral_banner_dismissed_at";
const REWARD_KEY = "crc_referral_reward_cad";
const TTL_DAYS = 7;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;
const DEFAULT_REWARD = 50;

export default function ReferralBanner() {
  const [hidden, setHidden] = useState(true);
  const [reward, setReward] = useState(DEFAULT_REWARD);

  useEffect(() => {
    try {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
      // Re-show after the TTL window so users with stale dismissals get
      // another chance — but never spam them inside a single session.
      const expired = !dismissedAt || (Date.now() - dismissedAt) > TTL_MS;
      const cached = Number(localStorage.getItem(REWARD_KEY) || 0);
      setHidden(!expired);
      if (cached > 0) setReward(cached);
    } catch { /* SSR / private mode */ }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setHidden(true);
  };

  if (hidden) return null;

  return (
    <div
      data-testid="referral-banner"
      className="sticky top-0 z-30 -mx-5 md:-mx-8 mb-5 px-5 md:px-8 py-3 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 text-white shadow-md"
    >
      <div className="flex items-center gap-3 max-w-6xl mx-auto">
        <div className="hidden sm:flex w-9 h-9 rounded-full bg-white/15 items-center justify-center ring-1 ring-white/25 flex-none">
          <Gift className="w-4.5 h-4.5"/>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm md:text-[15px] font-semibold leading-snug truncate">
            Refer a landlord — get a free month.
            <span className="hidden md:inline text-white/85 font-normal"> Invite a landlord friend and we'll credit one month of subscription onto your Central Rentals plan when they sign up.</span>
          </p>
        </div>
        <Link
          to="/referrals"
          data-testid="referral-banner-cta"
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white text-emerald-700 text-sm font-bold hover:bg-emerald-50 transition-colors whitespace-nowrap"
        >
          Get my link <ArrowRight className="w-3.5 h-3.5"/>
        </Link>
        <button
          type="button"
          onClick={dismiss}
          data-testid="referral-banner-dismiss"
          aria-label="Dismiss for now"
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/15 transition-colors flex-none"
        >
          <X className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}
